/*----------------------------------
- TLD APPLICATIONS PAGE
----------------------------------*/

/**
 * @docs docs/features/tld-applications
 * @adr  ADR-0065
 * @rule The shared applications board on the website: every application of ICANN's 2026 round in
 *       the string's alphabetical order, never ranked, coarse bands only. A row's Follow, the title
 *       pill and the phone bar open the free signup with the `follow-string` wall action; the board
 *       reads its search, views, filters and pages from the gateway after mount, and the path
 *       pages stay the canonical, crawlable list.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect } from 'preact/hooks';
import { AGENDA_PATH, agendaMonthOf } from '@uniquedomains/components/contracts/agenda/public';
import {
    TLD_APPLICATION_FOLLOW_ACTION,
    RADAR_CREATE_ACTION,
} from '@uniquedomains/components/contracts/upgrade/catalogs/featureActions';
import {
    TLD_APPLICATIONS_PATH,
    isTldApplicationsDefaultQuery,
    tldApplicationsPagePath,
    type PublicTldApplications,
} from '@uniquedomains/components/contracts/tlds/public';
import TldApplicationsBoard from '@uniquedomains/components/tlds/applications/TldApplicationsBoard';
import { useTldApplicationsQueryState } from '@uniquedomains/components/tlds/applications/useTldApplicationsQueryState';
import BoardRailCard from '@uniquedomains/components/boards/BoardRailCard';
import TldPagePill from '@uniquedomains/components/tlds/page/TldPagePill';
import CtaPill from '@uniquedomains/components/website/WebsiteCta';

import { AGENDA_NEWSLETTER_BAND_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildTldApplicationsGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import NewsletterInkBand from '@/client/pages/landing/components/newsletter/NewsletterInkBand';
import {
    CARD_BODY_CLASS,
    CARD_TITLE_CLASS,
    PAGE_CONTAINER_CLASS,
    WHITE_CARD_CLASS,
    WIDE_PAGE_CONTAINER_CLASS,
} from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { PRODUCT_PAGE_ROUTES } from '@/common/catalogs/appRoutesCatalog';
import { appErrorReporter } from '@/common/libs/errorReporting';
import { buildAgendaPageHref } from '@/common/libs/agenda/pageState';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';

import { fetchTldApplications } from '../../lib/fetchTldApplications';

import '../../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type TldApplicationsPageProps = {
    applications: PublicTldApplications;
    /** The request's UTC day: the timeline's "Now" and the pre-reveal countdown. */
    today: string;
    navigation?: LandingHeaderDomainsNavigation | null;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'tld_applications';
const SUBSCRIBE_ANCHOR_ID = 'subscribe';
/** Signup attribution for every CTA of the page (`User.desiredFrom`). */
const APPLICATIONS_ATTRIBUTION = 'website_tld_applications';

/** A follow lands the new member on Product's applications board, the string followed there once. */
const buildFollowHref = (string: string | null): string =>
    buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        from: APPLICATIONS_ATTRIBUTION,
        feature: 'radarsCount',
        action: TLD_APPLICATION_FOLLOW_ACTION,
        returnTo: string ? `${PRODUCT_PAGE_ROUTES.applications}?follow=${encodeURIComponent(string)}` : PRODUCT_PAGE_ROUTES.applications,
    });

const FOLLOW_HREF = buildFollowHref(null);

/**
 * The Watch card and the rail card keep the radar intent of their trigger
 * (`website_tld_applications_watch`): the account screen opens on the Radar wall copy.
 */
const WATCH_HREF = buildWebsiteAuthHref({
    flow: 'cold',
    mode: 'signup',
    from: APPLICATIONS_ATTRIBUTION,
    feature: 'radarsCount',
    action: RADAR_CREATE_ACTION,
});

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldApplicationsPage({ applications, today, navigation }: TldApplicationsPageProps) {
    const content = TLD_PAGE_CONTENT.applications;
    const board = useTldApplicationsQueryState({
        initial: applications,
        fetchPage: fetchTldApplications,
        onError: (error) => appErrorReporter.app.handleError(error),
    });
    const canonicalPath = tldApplicationsPagePath(applications.page);
    const relatedLinks = getHumanPageRelationships(TLD_APPLICATIONS_PATH).map((relationship) => ({
        label: relationship.label,
        href: relationship.targetPath,
    }));
    // The month is the clock's own, which the builder omits: the href is `/agenda?category=launches`
    // whatever the day, so the server render and the hydration agree.
    const launchesHref = buildAgendaPageHref({ month: agendaMonthOf(new Date(`${today}T00:00:00Z`)), category: 'launches', country: null, tld: null }, new Date(`${today}T00:00:00Z`));

    useEffect(() => {
        track('page_view', { page: TRACKING_PAGE, path: window.location.pathname + window.location.search, applicationsPage: applications.page });
    }, [applications.page]);

    const trackFollow = (section: string) => () =>
        trackConversionTrigger('website_tld_applications_follow', { href: FOLLOW_HREF, page: TRACKING_PAGE, section });
    const trackWatch = (section: string) => () =>
        trackConversionTrigger('website_tld_applications_watch', { href: WATCH_HREF, page: TRACKING_PAGE, section });

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation ?? EMPTY_LANDING_NAVIGATION}
                    currentPath={TLD_APPLICATIONS_PATH}
                    trackingPage={TRACKING_PAGE}
                    mobileActions={{
                        secondary: { label: content.mobileActions.agenda, href: AGENDA_PATH, iconName: 'calendar', testId: 'tld-applications-mobile-agenda' },
                        primary: {
                            label: content.mobileActions.follow,
                            href: FOLLOW_HREF,
                            onClick: trackFollow('tld_applications_mobile_bar'),
                            testId: 'tld-applications-mobile-follow',
                        },
                    }}
                />

                <main id="top" data-testid="tld-applications" data-page={applications.page}>
                    {/* The board reads at the agenda's 1500px so the full column tier fits beside the
                        rail (founder decision of 2026-10-03); the sections under it keep the 1240px page column. */}
                    <div class={`${WIDE_PAGE_CONTAINER_CLASS} pt-7 lg:pt-11`}>
                        <TldApplicationsBoard
                            surface="website"
                            testIdPrefix="tld-applications"
                            titleVisibility="always"
                            copy={content.board}
                            data={board.data}
                            state={board.state}
                            today={today}
                            busy={board.busy}
                            hrefs={{
                                agenda: launchesHref,
                                // The bare list's pages are real paths (crawlable, canonical); a
                                // searched or filtered list pages in place.
                                page: (page) => (isTldApplicationsDefaultQuery(board.state) ? tldApplicationsPagePath(page) : null),
                                followRow: buildFollowHref,
                            }}
                            onStateChange={(next) => board.change(next)}
                            onLockedAction={(_action, row) =>
                                trackConversionTrigger('website_tld_applications_follow_row', {
                                    href: buildFollowHref(row.string),
                                    page: TRACKING_PAGE,
                                    section: 'applications_row',
                                    context: { string: row.string },
                                })
                            }
                            titleActions={
                                <TldPagePill tone="ink" arrow href={FOLLOW_HREF} onClick={trackFollow('applications_title')} testId="tld-applications-follow-pill" attribution={APPLICATIONS_ATTRIBUTION}>
                                    {content.followPill}
                                </TldPagePill>
                            }
                            railExtra={
                                <BoardRailCard tone="ink" ariaLabel={content.rail.heading} testId="tld-applications-rail-card">
                                    <h2 class="m-0 text-[16px] font-bold leading-[1.3] tracking-[-0.01em] text-white">{content.rail.heading}</h2>
                                    <p class="mb-3.5 mt-2 text-[12.5px] leading-[1.5] text-white/70">{content.rail.body}</p>
                                    <TldPagePill tone="white" size="sm" href={WATCH_HREF} onClick={trackWatch('applications_rail')} testId="tld-applications-rail-cta" attribution={APPLICATIONS_ATTRIBUTION}>
                                        {content.rail.cta}
                                    </TldPagePill>
                                </BoardRailCard>
                            }
                        />
                    </div>

                    <section class={`${PAGE_CONTAINER_CLASS} pt-14 lg:pt-20`}>
                        <div class={`${WHITE_CARD_CLASS} flex flex-col gap-3 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-8`}>
                            <div class="max-w-[640px]">
                                <h2 class={CARD_TITLE_CLASS}>{content.watch.heading}</h2>
                                <p class={`${CARD_BODY_CLASS} mt-2`}>{content.watch.body}</p>
                            </div>
                            <CtaPill
                                href={WATCH_HREF}
                                variant="dark"
                                size="md"
                                data-testid="tld-applications-watch-cta"
                                data-attribution={APPLICATIONS_ATTRIBUTION}
                                onClick={trackWatch('applications_watch')}
                            >
                                {content.watch.cta}
                            </CtaPill>
                        </div>
                    </section>

                    <div class={`${PAGE_CONTAINER_CLASS} pt-6 lg:pt-8`}>
                        <NewsletterInkBand
                            anchorId={SUBSCRIBE_ANCHOR_ID}
                            source="website_tld_applications"
                            heading={AGENDA_NEWSLETTER_BAND_CONTENT.heading}
                            body={AGENDA_NEWSLETTER_BAND_CONTENT.body}
                        />
                    </div>

                    <RelatedLinks links={relatedLinks} title={content.relatedLinksTitle} />

                    <PageGeoBlock className="pb-4" geo={buildTldApplicationsGeoContext({ canonicalPath, pageNumber: applications.page })} surface="tld_applications" />
                </main>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
            </div>
        </div>
    );
}
